/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import { GraphRange, TouInterval } from "./Types";
import {
  getTouTier,
  getTouTierColor,
  normalizeTouName,
  TouTier,
} from "../components/Graphs/Common/touConfig";

export interface TouTooltipItem {
  key: string;
  name: string;
  color: string;
  tier: TouTier;
}

interface InternalSegment {
  fromMs: number;
  toMs: number;
  name: string;
  tier: TouTier;
  color: string;
}

const buildClippedSegments = (
  intervals: TouInterval[],
  displayRange: GraphRange,
): InternalSegment[] => {
  if (!intervals?.length) return [];
  const rangeStart = new Date(displayRange.start).getTime();
  const rangeStop = new Date(displayRange.stop).getTime();
  if (!Number.isFinite(rangeStart) || !Number.isFinite(rangeStop)) return [];
  if (rangeStop <= rangeStart) return [];

  const segments: InternalSegment[] = [];
  for (const interval of intervals) {
    const fromMs = new Date(interval.fromDateTime).getTime();
    const toMs = new Date(interval.toDateTime).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) continue;
    const startMs = Math.max(fromMs, rangeStart);
    const endMs = Math.min(toMs, rangeStop);
    if (endMs <= startMs) continue;
    const name = normalizeTouName(interval.touName) || "Unknown";
    segments.push({
      fromMs: startMs,
      toMs: endMs,
      name,
      tier: getTouTier(name),
      color: getTouTierColor(name),
    });
  }
  return segments;
};

/**
 * Returns a function that, given a recharts payload at hover position,
 * resolves the active TOU period (if any) at the hovered timestamp.
 *
 * Looks up by `payload[0].payload.unixTime`, so callers must enrich their
 * time-series data with a `unixTime` field aligned to the site timezone.
 */
export const generateGetActiveTouItem = (
  intervals: TouInterval[] | undefined,
  displayRange: GraphRange,
) => {
  const segments = buildClippedSegments(intervals ?? [], displayRange);
  return (payload: any): TouTooltipItem | null => {
    if (!payload || payload.length === 0) return null;
    const unixTime = payload[0]?.payload?.unixTime;
    if (typeof unixTime !== "number" || !Number.isFinite(unixTime)) return null;
    const match = segments.find(
      (s) => unixTime >= s.fromMs && unixTime < s.toMs,
    );
    if (!match) return null;
    return {
      key: match.name,
      name: match.name,
      color: match.color,
      tier: match.tier,
    };
  };
};

/**
 * Returns the unique set of TOU tiers present in the visible range, ordered
 * from lowest (cheapest) to highest cost. Used to populate the static legend.
 */
export const generateStaticTouItems = (
  intervals: TouInterval[] | undefined,
  displayRange: GraphRange,
): TouTooltipItem[] => {
  const segments = buildClippedSegments(intervals ?? [], displayRange);
  const seen = new Map<string, TouTooltipItem>();
  for (const seg of segments) {
    if (seen.has(seg.name)) continue;
    seen.set(seg.name, {
      key: seg.name,
      name: seg.name,
      color: seg.color,
      tier: seg.tier,
    });
  }
  return Array.from(seen.values()).sort((a, b) => a.tier - b.tier);
};
